<script setup>
// SiteUp (M7): cartão de evento do WhatsApp.
//
// O backend entrega content_attributes.event pronto. Preferimos `starts_at`
// (ISO) para formatar na locale do usuário; `start_time` fica como fallback
// textual quando o parse do texto do WAHA não rendeu ISO.
//
// Atalho de calendário externo ficou fora do escopo aprovado.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes } = useMessageContext();
const { t } = useI18n();
const { resolvedLocale } = useLocale();

const event = computed(() => contentAttributes.value?.event ?? {});

// O backend grava snake_case (`starts_at`, `start_time`), mas o fio cameliza
// tudo em profundidade antes de entregar às bolhas (`MessageList.vue`,
// `useCamelCase(..., { deep: true })`). Ler só o snake_case devolvia `undefined`
// SEMPRE: o bloco de dia/mês sumia e o "Quarta, 14:00" — que é o ponto do
// cartão — nunca aparecia. Aceitar as duas grafias mantém o componente correto
// nos dois caminhos, inclusive quando o fork passar a mandar estruturado.
const startsAtRaw = computed(
  () => event.value.startsAt || event.value.starts_at || ''
);
const startTimeRaw = computed(
  () => event.value.startTime || event.value.start_time || ''
);

const startDate = computed(() => {
  const iso = startsAtRaw.value || startTimeRaw.value;
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
});

const dayNumber = computed(() => {
  if (!startDate.value) return '';
  return new Intl.DateTimeFormat(resolvedLocale.value, {
    day: 'numeric',
  }).format(startDate.value);
});

const monthShort = computed(() => {
  if (!startDate.value) return '';
  return new Intl.DateTimeFormat(resolvedLocale.value, { month: 'short' })
    .format(startDate.value)
    .replace(/\.$/, '');
});

// "Quarta, 14:00" — dia da semana ESCRITO, na locale do usuário.
const whenLine = computed(() => {
  if (startDate.value) {
    const weekday = new Intl.DateTimeFormat(resolvedLocale.value, {
      weekday: 'long',
    }).format(startDate.value);
    const time = new Intl.DateTimeFormat(resolvedLocale.value, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(startDate.value);
    const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    const base = `${capitalized}, ${time}`;
    return event.value.location ? `${base} · ${event.value.location}` : base;
  }

  const raw = startTimeRaw.value;
  if (!raw && !event.value.location) return '';
  if (raw && event.value.location) return `${raw} · ${event.value.location}`;
  return raw || event.value.location || '';
});
</script>

<template>
  <BaseBubble class="p-3" data-bubble-name="event">
    <div class="flex flex-col gap-2 min-w-[15rem]">
      <div class="flex gap-3 items-start">
        <div
          v-if="startDate"
          class="flex flex-col items-center justify-center shrink-0 size-12 rounded-xl bg-current/10"
          aria-hidden="true"
        >
          <span class="text-lg font-semibold leading-none tabular-nums">
            {{ dayNumber }}
          </span>
          <span class="text-[10px] uppercase tracking-wide opacity-80 mt-0.5">
            {{ monthShort }}
          </span>
        </div>
        <div
          v-else
          class="flex items-center justify-center shrink-0 size-12 rounded-xl bg-current/10"
          aria-hidden="true"
        >
          <Icon icon="i-ph-calendar-blank" class="size-5 opacity-80" />
        </div>

        <div class="flex flex-col min-w-0 gap-0.5">
          <p class="text-sm font-medium m-0">
            {{ event.name || t('CONVERSATION.EVENT.TITLE') }}
          </p>
          <p v-if="whenLine" class="text-xs opacity-80 m-0">
            {{ whenLine }}
          </p>
        </div>
      </div>

      <p
        v-if="event.description"
        class="text-sm opacity-80 m-0 whitespace-pre-line pt-1 border-t border-current/15"
      >
        {{ event.description }}
      </p>
    </div>
  </BaseBubble>
</template>
